<template>
  <div
    class="table-splitter-container"
    :class="{ 'is-dragging': isDragging }"
    @mousedown="handleMouseDown"
  >
    <!-- 全宽交互轨道与视觉线条 -->
    <div class="splitter-track">
      <div class="splitter-bar">
        <div class="splitter-handle-pill">
          <span class="grip-dot"></span>
          <span class="grip-dot"></span>
          <span class="grip-dot"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onBeforeUnmount } from 'vue'

export default defineComponent({
  name: 'TableSplitter',
  emits: ['drag-start', 'drag-move', 'drag-end'],
  setup(props, { emit }) {
    const isDragging = ref(false)
    let startClientY = 0

    const handleMouseDown = (e: MouseEvent) => {
      // 仅响应鼠标左键
      if (e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()

      isDragging.value = true
      startClientY = e.clientY

      emit('drag-start', { startY: e.clientY })

      document.body.style.userSelect = 'none'
      document.body.style.cursor = 'row-resize'

      window.addEventListener('mousemove', handleMouseMove, { passive: false })
      window.addEventListener('mouseup', handleMouseUp, { passive: false })
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.value) return
      e.preventDefault()
      const deltaY = e.clientY - startClientY
      emit('drag-move', { deltaY, clientY: e.clientY })
    }

    const handleMouseUp = (e: MouseEvent) => {
      if (!isDragging.value) return
      isDragging.value = false

      document.body.style.userSelect = ''
      document.body.style.cursor = ''

      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)

      emit('drag-end', { clientY: e.clientY })
    }

    onBeforeUnmount(() => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    })

    return {
      isDragging,
      handleMouseDown
    }
  }
})
</script>

<style lang="scss" scoped>
.table-splitter-container {
  width: 100%;
  height: 14px;
  margin: 4px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: row-resize;
  user-select: none;
  position: relative;
  z-index: 10;
  box-sizing: border-box;

  .splitter-track {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .splitter-bar {
    width: 100%;
    height: 2px;
    background: var(--line-strong, #e2e2e2);
    border-radius: 1px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: height 0.18s cubic-bezier(0.16, 1, 0.3, 1),
                background 0.18s ease,
                border-radius 0.18s ease,
                box-shadow 0.18s ease;

    .splitter-handle-pill {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 0 12px;
      height: 10px;
      background: var(--surface-main, #ffffff);
      border: 1px solid var(--line-strong, #e2e2e2);
      border-radius: 999px;
      opacity: 0.7;
      transition: all 0.18s ease;

      .grip-dot {
        display: block;
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background: var(--ink-soft, #7e888c);
        transition: all 0.18s ease;
      }
    }
  }

  // 鼠标悬停态：线加粗并高亮为品牌蓝
  &:hover:not(.is-dragging) {
    .splitter-bar {
      height: 3px;
      background: var(--blue, #236de9);
      box-shadow: 0 0 6px rgba(35, 109, 233, 0.35);

      .splitter-handle-pill {
        opacity: 1;
        border-color: var(--blue, #236de9);
        box-shadow: 0 0 8px rgba(35, 109, 233, 0.25);

        .grip-dot {
          background: var(--blue, #236de9);
        }
      }
    }
  }

  // 长按拖动态：线条变身半透明长框，随着拖拽上下移动
  &.is-dragging {
    .splitter-bar {
      height: 14px;
      border-radius: 6px;
      background: rgba(35, 109, 233, 0.18) !important;
      border: 1.5px solid var(--blue, #236de9) !important;
      box-shadow: 0 2px 14px rgba(35, 109, 233, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.4);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);

      .splitter-handle-pill {
        background: transparent;
        border-color: transparent;
        opacity: 1;

        .grip-dot {
          width: 4px;
          height: 4px;
          background: var(--blue, #236de9);
          box-shadow: 0 0 4px var(--blue, #236de9);
        }
      }
    }
  }
}
</style>
